// Connections — §9's fourth section.
//
// "central place for accounts, credentials, IDs and config for external systems
// (Meta, Google, TikTok, Taboola, RedTrack, Everflow, Cake, Shopify, Klaviyo,
// Telegram, email, calendar, Drive, GitHub, …). System-wide or user-specific
// per integration."
//
// PRESERVES, from the v1 `/settings` page:
//   Connected Meta Ad Accounts   GET    /api/meta/accounts
//                                DELETE /api/meta/accounts/{id}
//                                POST   /api/meta/accounts/refresh
//                                       /api/meta/oauth/login  (full-page)
//
// WHAT §9 NAMES AND THE TREE DOES NOT HAVE
// ========================================
// §9 lists fourteen external systems. Exactly one of them — Meta — has account
// management endpoints. Two more have a read-only status probe: Telegram and
// Trello. The rest have working *integrations* (RedTrack pulls, Shopify and
// Klaviyo syncs, Everflow) whose credentials live in environment variables and
// are not exposed or settable through any endpoint.
//
// That is a finding, not a task. R4 forbids writing the endpoint that would let
// this section manage them, and R6 forbids drawing a credentials form that
// posts nowhere. So the systems Cipher genuinely talks to are listed with what
// is actually known about each, and the ones whose configuration is
// environment-only say so in those words. An operator reading this page learns
// the true state of Cipher's connections, which is the point of the section.
//
// SECRETS ARE NEVER RENDERED
// ==========================
// No endpoint here returns a token and none is requested. `/telegram/status`
// answers whether the bot token is set and whether the bot answers — a boolean
// and a reachability check, never the value. That is the only shape in which a
// credential appears on this page.
import React, { useState } from "react";

import { ENDPOINTS, del, metaOAuthUrl, post } from "../api";
import { Block, Button, Gate, Row, StatusDot, Table, Value } from "../ui";
import useResource from "../useResource";

/**
 * The systems §9 names, and where each one's configuration actually lives.
 *
 * `mode` is the honest answer to "can this be configured here?":
 *   managed   accounts are connected and disconnected on this page
 *   probe     a read-only status endpoint exists
 *   env       configured by environment variable; no endpoint exposes it
 */
const SYSTEMS = [
  { name: "Meta", mode: "managed", detail: "OAuth. Ad accounts are connected below." },
  { name: "Telegram", mode: "probe", detail: "Bot token in environment; reachability checked live." },
  { name: "Trello", mode: "probe", detail: "Board and list IDs in environment." },
  { name: "RedTrack", mode: "env", detail: "API key in environment. Pulls run on a schedule." },
  { name: "Everflow", mode: "env", detail: "API key in environment." },
  { name: "Shopify", mode: "env", detail: "ABH arm. Credentials in environment; sync is on demand." },
  { name: "Klaviyo", mode: "env", detail: "ABH arm. Credentials in environment; sync is on demand." },
  { name: "Google", mode: "none", detail: "Named in §9. No integration in the tree today." },
  { name: "TikTok", mode: "none", detail: "Named in §9. No integration in the tree today." },
  { name: "Taboola", mode: "none", detail: "Named in §9. No integration in the tree today." },
  { name: "Cake", mode: "none", detail: "Named in §9. No integration in the tree today." },
  { name: "Email", mode: "none", detail: "Named in §9. No integration in the tree today." },
  { name: "Calendar", mode: "none", detail: "Named in §9. No integration in the tree today." },
  { name: "Drive", mode: "none", detail: "Named in §9. No integration in the tree today." },
  { name: "GitHub", mode: "none", detail: "Named in §9. No integration in the tree today." },
];

const MODE_LABEL = {
  managed: "Managed here",
  probe: "Status only",
  env: "Environment",
  none: "Not integrated",
};

function MetaAccounts() {
  const accounts = useResource(ENDPOINTS.metaAccounts, {
    isEmpty: (d) => !(d?.accounts || []).length,
  });
  const [busy, setBusy] = useState("");

  async function disconnect(id) {
    setBusy(id);
    await del(`${ENDPOINTS.metaAccounts}/${id}`);
    setBusy("");
    accounts.reload();
  }

  async function refresh() {
    setBusy("refresh");
    await post(ENDPOINTS.metaAccountsRefresh, {});
    setBusy("");
    accounts.reload();
  }

  return (
    <Block
      title="Meta ad accounts"
      note="Connected through Meta's OAuth. Founders see every account; operators see their own."
      actions={
        <span className="set-inline">
          <Button onClick={refresh} disabled={busy === "refresh"} kind="quiet">
            {busy === "refresh" ? "Refreshing…" : "Refresh"}
          </Button>
          {/* A full-page redirect, not an XHR — the OAuth handshake leaves the app. */}
          <a className="btn-ghost btn-sm" href={metaOAuthUrl()}>
            Connect account
          </a>
        </span>
      }
    >
      <Gate resource={accounts} empty="No Meta ad accounts connected.">
        {(data) => (
          <Table head={["Account", "Account ID", "Status", ""]}>
            {(data.accounts || []).map((a) => {
              const id = a.meta_account_id || a.account_id || a.id;
              return (
                <tr key={id}>
                  <td>{a.name || a.account_name || "—"}</td>
                  <td className="mono dim">{id}</td>
                  <td className="mono">{a.status || "—"}</td>
                  <td className="set-cell-action">
                    <Button
                      onClick={() => disconnect(id)}
                      disabled={busy === id}
                      kind="quiet"
                    >
                      {busy === id ? "Disconnecting…" : "Disconnect"}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </Table>
        )}
      </Gate>
    </Block>
  );
}

function TelegramConnection() {
  // Founder-gated on the server. A non-founder gets `denied`, which the Gate
  // renders as a refusal rather than a fault.
  const status = useResource(ENDPOINTS.telegramStatus);

  return (
    <Block title="Telegram" note="System-wide bot. Per-user identifiers live in My Settings.">
      <Gate resource={status}>
        {(d) => (
          <>
            <Row label="Bot token" hint="Whether the token is present. The value is never shown.">
              <StatusDot
                tone={d.env_set ? "green" : "red"}
                label={d.env_set ? "Configured" : "Not set"}
              />
            </Row>
            <Row label="Bot reachable" hint="Live check against Telegram.">
              <StatusDot
                tone={d.bot_reachable ? "green" : "red"}
                label={d.bot_reachable ? "Responding" : "Not responding"}
              />
            </Row>
          </>
        )}
      </Gate>
    </Block>
  );
}

function TrelloConnection() {
  const status = useResource(ENDPOINTS.trelloStatus);

  return (
    <Block title="Trello" note="Board and list identifiers used by the operations webhook.">
      <Gate resource={status}>
        {(d) => (
          <>
            <Row label="Configured">
              <StatusDot
                tone={d.configured ? "green" : "yellow"}
                label={d.configured ? "Configured" : "Not configured"}
              />
            </Row>
            <Row label="Board ID">
              <Value mono>{d.board_id}</Value>
            </Row>
            <Row label="Lists">
              <Value>{(d.lists || []).join(" · ")}</Value>
            </Row>
          </>
        )}
      </Gate>
    </Block>
  );
}

function SystemInventory() {
  return (
    <Block
      title="External systems"
      note="Every system §9 names, and where each one is actually configured today."
    >
      <Table head={["System", "Configuration", "Detail"]}>
        {SYSTEMS.map((s) => (
          <tr key={s.name}>
            <td>{s.name}</td>
            <td className="mono dim">{MODE_LABEL[s.mode]}</td>
            <td className="dim">{s.detail}</td>
          </tr>
        ))}
      </Table>
    </Block>
  );
}

export default function Connections() {
  return (
    <>
      <MetaAccounts />
      <TelegramConnection />
      <TrelloConnection />
      <SystemInventory />
    </>
  );
}
